import { Helmet } from "react-helmet-async";

const SITE = "https://www.neuronest.global";
const DEFAULT_IMAGE = `${SITE}/og-image.png`;
const SITE_NAME = "NeuroNest";

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — Sensory-Safe Companion App for Neurodivergent Individuals`;
  const url = `${SITE}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Language */}
      <html lang="en-GB" />
      <link rel="alternate" hrefLang="en-GB" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Reusable schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NeuroNest",
  url: SITE,
  logo: `${SITE}/logo.png`,
  description:
    "A sensory-safe companion app that helps neurodivergent individuals navigate daily life with greater ease, predictability, and self-compassion.",
  contactPoint: {
    "@type": "ContactPoint",
    email: "hello@neuronest.global",
    contactType: "customer support",
  },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "NeuroNest",
  url: SITE,
  description:
    "Sensory-safe companion app for neurodivergent individuals with calming tools, visual routines, communication cards, and sensory-friendly place discovery.",
  publisher: { "@id": `${SITE}/#organization` },
};

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

export function softwareAppSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "NeuroNest",
    operatingSystem: "Android",
    applicationCategory: "HealthApplication",
    description:
      "A sensory-safe companion app for neurodivergent individuals with calming tools, visual routines, communication cards, and 1,370+ sensory-friendly places.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "GBP",
      description: "Monthly subscription available",
    },
    author: organizationSchema,
  };
}

export function videoSchema(video) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.title,
    description: video.description,
    uploadDate: "2025-12-01",
    publisher: organizationSchema,
  };
}
